import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import BlockIcon from '@mui/icons-material/Block';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import api from '../../api/api';

const SOURCE_TYPE_PEDIDO = 'pedido';
const SOURCE_TYPE_PENDING_DTE = 'pedido_pendiente_dte';
const SOURCE_TYPE_MANTENCION = 'automotriz_mantencion';

const TAB_PEDIDOS = 'pedidos';
const TAB_MANTENCIONES = 'mantenciones';

const DEFAULT_LIMIT = 50;
const SEARCH_LIMIT = 100;

const toStr = (value) => (value == null ? '' : String(value));
const toNum = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

function toBool(value) {
  if (value === true) return true;
  if (value === false) return false;
  const normalized = toStr(value).trim().toLowerCase();
  if (['true', 't', '1', 'si', 'sí', 'yes', 'y'].includes(normalized)) return true;
  if (['false', 'f', '0', 'no', 'n'].includes(normalized)) return false;
  return false;
}

function cleanUpper(value) {
  return toStr(value)
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function safeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isPositiveId(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0;
}

function hasValidDteFolio(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function formatQty(value) {
  const parsed = toNum(value);
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(parsed);
}

function formatDocType(value) {
  const normalized = cleanUpper(value);
  if (['BOLETA', '39', 'DTE39'].includes(normalized)) return 'Boleta';
  if (['FACTURA', '33', 'DTE33'].includes(normalized)) return 'Factura';
  if (['GUIA', 'GUIA_DESPACHO', '52', 'DTE52'].includes(normalized)) return 'Guía';
  return value ? toStr(value) : 'DTE';
}

function getEstadoMeta(value) {
  const normalized = cleanUpper(value);
  if (['APPROVED', 'APROBADO', 'PAGADO', 'PAID'].includes(normalized)) {
    return { label: 'Aprobado', color: 'success' };
  }
  if (['PENDING', 'PENDIENTE'].includes(normalized)) {
    return { label: 'Pendiente', color: 'warning' };
  }
  if (['CANCELADO', 'CANCELLED', 'ANULADO', 'ANULADA'].includes(normalized)) {
    return { label: 'Cancelado', color: 'error' };
  }
  return { label: value || 'Sin estado', color: 'default' };
}

function getMantencionEstadoMeta(value) {
  const normalized = cleanUpper(value);
  const map = {
    BORRADOR: { label: 'Borrador', color: 'default' },
    EN_PROCESO: { label: 'En proceso', color: 'info' },
    ESPERANDO_APROBACION: { label: 'Esperando aprobación', color: 'warning' },
    ESPERANDO_REPUESTOS: { label: 'Esperando repuestos', color: 'warning' },
    LISTA_PARA_COBRO: { label: 'Lista para cobro', color: 'secondary' },
    COMPLETADA: { label: 'Completada', color: 'success' },
    ENTREGADA: { label: 'Entregada', color: 'success' },
    ANULADA: { label: 'Anulada', color: 'error' },
  };
  return map[normalized] || { label: value || 'Sin estado', color: 'default' };
}

function getItemsPendientesFromPedido(pedido) {
  const candidates = [
    pedido?.items_para_pos,
    pedido?.items_pendientes_facturacion,
    pedido?.facturacion?.items_pendientes,
    pedido?.estados?.facturacion?.items_pendientes,
  ];
  return candidates.find((rows) => Array.isArray(rows) && rows.length) || [];
}

function getModoImportacionPedido(pedido) {
  return (
    pedido?.modo_importacion ||
    pedido?.facturacion?.modo_importacion ||
    pedido?.estados?.facturacion?.modo_importacion ||
    SOURCE_TYPE_PEDIDO
  );
}

function isPedidoPendienteDte(pedido) {
  return (
    getModoImportacionPedido(pedido) === SOURCE_TYPE_PENDING_DTE ||
    pedido?.estado_facturacion === 'DTE_PARCIAL_PENDIENTE' ||
    pedido?.facturacion?.codigo === 'DTE_PARCIAL_PENDIENTE' ||
    pedido?.estados?.facturacion?.codigo === 'DTE_PARCIAL_PENDIENTE' ||
    toBool(pedido?.puede_emitir_pendientes) ||
    toBool(pedido?.facturacion?.puede_emitir_pendientes) ||
    toBool(pedido?.estados?.facturacion?.puede_emitir_pendientes)
  );
}

function extractDteInfo(row) {
  const facturacion = row?.facturacion && typeof row.facturacion === 'object'
    ? row.facturacion
    : row?.estados?.facturacion && typeof row.estados.facturacion === 'object'
      ? row.estados.facturacion
      : {};

  const dte = row?.dte || row?.documento_tributario || row?.venta?.dte || facturacion?.dte || {};
  const idVenta = row?.id_venta ?? row?.venta_id ?? row?.venta?.id_venta ?? row?.venta?.id ?? facturacion?.id_venta;
  const tipoDocumento = row?.tipo_documento ?? row?.venta?.tipo_documento ?? facturacion?.tipo_documento ?? dte?.tipo_documento ?? dte?.tipo;
  const folio = row?.folio_dte ?? row?.dte_folio ?? facturacion?.folio ?? dte?.folio;
  const idDte = row?.id_dte ?? row?.dte_id ?? facturacion?.dte_id ?? dte?.id;
  const itemsPendientes = getItemsPendientesFromPedido(row);
  const cantidadPendiente = toNum(row?.cantidad_pendiente_dte ?? facturacion?.cantidad_pendiente, 0);
  const estadoFacturacion = cleanUpper(row?.estado_facturacion || facturacion?.codigo || '');
  const puedeEmitirPendientes =
    toBool(row?.puede_emitir_pendientes) ||
    toBool(facturacion?.puede_emitir_pendientes) ||
    (estadoFacturacion === 'DTE_PARCIAL_PENDIENTE' && cantidadPendiente > 0 && itemsPendientes.length > 0);

  const hasVenta = isPositiveId(idVenta);
  const hasDte = hasVenta || isPositiveId(idDte) || hasValidDteFolio(folio) || toBool(facturacion?.tiene_dte);
  const blocked = hasDte && !puedeEmitirPendientes;

  return {
    idVenta: hasVenta ? Number(idVenta) : null,
    tipoDocumento: tipoDocumento || null,
    folio: hasValidDteFolio(folio) ? toStr(folio) : null,
    hasDte,
    blocked,
    puedeEmitirPendientes,
    cantidadPendiente,
    itemsPendientes,
    modoImportacion: puedeEmitirPendientes ? SOURCE_TYPE_PENDING_DTE : SOURCE_TYPE_PEDIDO,
    blockReason: blocked
      ? `${formatDocType(tipoDocumento)} ya emitido${hasVenta ? ` · Venta #${idVenta}` : ''}`
      : '',
  };
}

function normalizePedidosRows(data) {
  const rows = data?.pedidos || data?.results || data?.data || [];
  return (Array.isArray(rows) ? rows : [])
    .map((raw) => {
      const id = Number(raw?.id ?? raw?.folio ?? raw?.pedido_id);
      const cliente = raw?.cliente && typeof raw.cliente === 'object' ? raw.cliente : {};
      const operativa = raw?.operativa && typeof raw.operativa === 'object' ? raw.operativa : {};
      const totales = raw?.totales && typeof raw.totales === 'object' ? raw.totales : {};
      const dte = extractDteInfo(raw);
      return {
        kind: TAB_PEDIDOS,
        id,
        cliente: raw?.nombre_cliente || raw?.cliente_nombre || raw?.razon_social || cliente?.nombre || cliente?.razon_social || '—',
        rut: raw?.rut_cliente || raw?.cliente_rut || cliente?.rut || '',
        fecha: safeDate(raw?.fecha_pedido || raw?.fecha || raw?.created_at),
        total: toNum(totales?.total ?? raw?.total ?? raw?.total_bruto),
        subtotal: toNum(totales?.subtotal ?? raw?.subtotal ?? raw?.monto_neto),
        impuesto: toNum(totales?.impuesto_total ?? raw?.impuesto_total ?? raw?.iva),
        estado: raw?.estado || '',
        estadoMeta: getEstadoMeta(raw?.estado),
        origen: raw?.origen || '',
        vin: operativa?.vin || raw?.vin || '',
        telefono: operativa?.telefono || raw?.telefono || '',
        ...dte,
        raw,
      };
    })
    .filter((row) => Number.isInteger(row.id) && row.id > 0)
    .sort((a, b) => {
      if (a.puedeEmitirPendientes !== b.puedeEmitirPendientes) return a.puedeEmitirPendientes ? -1 : 1;
      if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
      return b.id - a.id;
    });
}

function normalizeMantencionesRows(data) {
  const rows = data?.mantenciones || data?.results || data?.data || [];
  return (Array.isArray(rows) ? rows : [])
    .map((raw) => {
      const id = Number(raw?.id ?? raw?.mantencion_id);
      const estadoMeta = getMantencionEstadoMeta(raw?.estado);
      const ventaId = Number(raw?.venta_id || 0) || null;
      const blocked = Boolean(ventaId) || ['COMPLETADA', 'ENTREGADA', 'ANULADA'].includes(cleanUpper(raw?.estado));
      const vehicleName = [raw?.marca, raw?.modelo, raw?.version, raw?.anio].filter(Boolean).join(' ');
      return {
        kind: TAB_MANTENCIONES,
        id,
        codigo: raw?.codigo || `Mantención #${id}`,
        cliente: raw?.cliente_nombre || raw?.nombre_cliente || 'Sin cliente',
        rut: raw?.cliente_rut || '',
        fecha: safeDate(raw?.fecha_ingreso || raw?.fecha_creacion),
        total: toNum(raw?.total),
        subtotal: toNum(raw?.subtotal_neto),
        impuesto: toNum(raw?.total_impuestos),
        estado: raw?.estado || '',
        estadoMeta,
        patente: raw?.patente || raw?.identificador || '',
        vin: raw?.vin || '',
        vehiculo: vehicleName || raw?.vehiculo_descripcion || '',
        kilometraje: raw?.kilometraje_actual,
        totalItems: toNum(raw?.total_items),
        totalReservas: toNum(raw?.total_reservas_activas),
        ventaId,
        blocked,
        blockReason: ventaId ? `Vinculada a venta #${ventaId}` : blocked ? `Estado ${estadoMeta.label}` : '',
        raw,
      };
    })
    .filter((row) => Number.isInteger(row.id) && row.id > 0)
    .sort((a, b) => b.id - a.id);
}

function buildPedidoForPOS(detalle, sourceType) {
  const pending = sourceType === SOURCE_TYPE_PENDING_DTE || isPedidoPendienteDte(detalle);
  const pendingItems = getItemsPendientesFromPedido(detalle);
  const items = pending ? pendingItems : Array.isArray(detalle?.items) ? detalle.items : [];
  return {
    ...detalle,
    modo_importacion: pending ? SOURCE_TYPE_PENDING_DTE : SOURCE_TYPE_PEDIDO,
    items,
    items_para_pos: items,
    items_pendientes_facturacion: pendingItems,
    facturacion: {
      ...(detalle?.facturacion || {}),
      items_pendientes: pendingItems,
      modo_importacion: pending ? SOURCE_TYPE_PENDING_DTE : SOURCE_TYPE_PEDIDO,
      puede_emitir_pendientes: pending,
    },
  };
}

function SummaryCard({ title, value, helper, icon, color = 'primary' }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'grey.100', color: `${color}.main` }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{title}</Typography>
          <Typography variant="h6" fontWeight={900}>{value}</Typography>
          {helper ? <Typography variant="caption" color="text.secondary">{helper}</Typography> : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function PedidoCard({ row, fmtCLP, fmtDate, disabled, importingKey, onImport }) {
  const busy = importingKey === `pedido:${row.id}`;
  const available = !disabled && !row.blocked && !importingKey;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, opacity: row.blocked ? 0.68 : 1, borderColor: row.puedeEmitirPendientes ? 'warning.main' : 'divider' }}>
      <CardContent>
        <Stack spacing={1.4}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" fontWeight={900}>Pedido #{row.id}</Typography>
                <Chip size="small" label={row.estadoMeta.label} color={row.estadoMeta.color} />
                {row.puedeEmitirPendientes ? (
                  <Chip size="small" icon={<ReceiptLongIcon />} label={`DTE parcial · ${formatQty(row.cantidadPendiente)} pendiente(s)`} color="warning" />
                ) : row.blocked ? (
                  <Chip size="small" icon={<BlockIcon />} label={row.blockReason || 'No disponible'} color="error" variant="outlined" />
                ) : (
                  <Chip size="small" icon={<CheckCircleIcon />} label="Disponible" color="success" variant="outlined" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">{row.fecha ? fmtDate.format(row.fecha) : 'Sin fecha'}</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="h6" fontWeight={900}>{fmtCLP.format(row.total)}</Typography>
              <Typography variant="caption" color="text.secondary">Neto {fmtCLP.format(row.subtotal)} · IVA {fmtCLP.format(row.impuesto)}</Typography>
            </Box>
          </Stack>
          <Divider />
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}><PersonIcon color="action" fontSize="small" /><Box><Typography variant="body2" fontWeight={800}>{row.cliente}</Typography><Typography variant="caption" color="text.secondary">{row.rut || 'RUT —'}</Typography></Box></Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}><DirectionsCarIcon color="action" fontSize="small" /><Box><Typography variant="body2">VIN: <strong>{row.vin || '—'}</strong></Typography><Typography variant="caption" color="text.secondary">{row.telefono || 'Teléfono —'}</Typography></Box></Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}><LocalShippingIcon color="action" fontSize="small" /><Typography variant="body2">{row.origen || 'Origen —'}</Typography></Stack>
            </Grid>
          </Grid>
          {row.puedeEmitirPendientes && row.itemsPendientes.length ? (
            <Alert severity="warning" icon={<Inventory2Icon />}>{row.itemsPendientes.length} línea(s) pendiente(s) serán cargadas al POS.</Alert>
          ) : null}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color={row.puedeEmitirPendientes ? 'warning' : 'primary'}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : row.puedeEmitirPendientes ? <ReceiptLongIcon /> : <FileDownloadIcon />}
              disabled={!available}
              onClick={() => onImport(row)}
            >
              {busy ? 'Importando…' : row.puedeEmitirPendientes ? 'Emitir pendientes' : 'Importar a POS'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function MantencionCard({ row, fmtCLP, fmtDate, disabled, importingKey, onImport }) {
  const busy = importingKey === `mantencion:${row.id}`;
  const available = !disabled && !row.blocked && !importingKey;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2.5, opacity: row.blocked ? 0.68 : 1, borderColor: row.blocked ? 'divider' : 'secondary.main' }}>
      <CardContent>
        <Stack spacing={1.4}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <BuildCircleOutlinedIcon color="secondary" />
                <Typography variant="h6" fontWeight={900}>{row.codigo}</Typography>
                <Chip size="small" label={row.estadoMeta.label} color={row.estadoMeta.color} />
                {row.blocked ? <Chip size="small" icon={<BlockIcon />} label={row.blockReason || 'No disponible'} color="error" variant="outlined" /> : <Chip size="small" icon={<CheckCircleIcon />} label="Lista para cobrar" color="success" variant="outlined" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">Ingreso {row.fecha ? fmtDate.format(row.fecha) : 'sin fecha'}</Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="h6" fontWeight={900}>{fmtCLP.format(row.total)}</Typography>
              <Typography variant="caption" color="text.secondary">Neto {fmtCLP.format(row.subtotal)} · IVA {fmtCLP.format(row.impuesto)}</Typography>
            </Box>
          </Stack>
          <Divider />
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}><PersonIcon color="action" fontSize="small" /><Box><Typography variant="body2" fontWeight={800}>{row.cliente}</Typography><Typography variant="caption" color="text.secondary">{row.rut || 'RUT —'}</Typography></Box></Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1}><DirectionsCarIcon color="action" fontSize="small" /><Box><Typography variant="body2" fontWeight={800}>{row.patente || 'Sin patente'} · {row.vehiculo || 'Vehículo'}</Typography><Typography variant="caption" color="text.secondary">VIN {row.vin || '—'}{row.kilometraje != null ? ` · ${formatQty(row.kilometraje)} km` : ''}</Typography></Box></Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}><Inventory2Icon color="action" fontSize="small" /><Box><Typography variant="body2"><strong>{row.totalItems}</strong> ítem(s)</Typography><Typography variant="caption" color="text.secondary">{row.totalReservas} reserva(s)</Typography></Box></Stack>
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              color="secondary"
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ShoppingCartCheckoutIcon />}
              disabled={!available}
              onClick={() => onImport(row)}
            >
              {busy ? 'Preparando…' : 'Cargar mantención'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ImportDocToPOSModal({ open, onClose, onImport, disabled = false }) {
  const [tab, setTab] = useState(TAB_PEDIDOS);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [importingKey, setImportingKey] = useState(null);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showOnlyPendingDte, setShowOnlyPendingDte] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT, totalPages: 1 });

  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);

  const fmtCLP = useMemo(() => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }), []);
  const fmtDate = useMemo(() => new Intl.DateTimeFormat('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit' }), []);

  const fetchRows = async ({ nextTab = tab, queryArg = q, nextPageArg = page } = {}) => {
    const query = toStr(queryArg).trim();
    const nextPage = Math.max(1, Number(nextPageArg) || 1);
    const effectiveLimit = query ? SEARCH_LIMIT : DEFAULT_LIMIT;
    const requestId = requestSeqRef.current + 1;
    requestSeqRef.current = requestId;
    setLoading(true);

    try {
      let data;
      let normalized;
      if (nextTab === TAB_MANTENCIONES) {
        const response = await api.get('/automotriz_mantenciones', {
          params: {
            q: query || undefined,
            estado: 'LISTA_PARA_COBRO',
            modo_registro: 'OPERACIONAL',
            orden: 'fecha_desc',
            page: nextPage,
            limit: effectiveLimit,
          },
        });
        data = response.data;
        normalized = normalizeMantencionesRows(data);
      } else {
        const response = await api.get('/ventas_pedidos', {
          params: {
            buscar: query,
            search: query,
            page: nextPage,
            limit: effectiveLimit,
            limite: effectiveLimit,
            incluir_dte: true,
            incluir_venta: true,
            incluir_facturacion: true,
            incluir_items_pendientes: true,
            solo_importables_pos: true,
            priorizar_pendientes_facturacion: true,
          },
        });
        data = response.data;
        normalized = normalizePedidosRows(data);
      }

      if (requestSeqRef.current !== requestId) return;
      const total = Number(data?.meta?.total ?? normalized.length ?? 0);
      const limit = Number(data?.meta?.limit || effectiveLimit);
      const backendPage = Number(data?.meta?.page || nextPage);
      const totalPages = Math.max(1, Number(data?.meta?.totalPages ?? data?.meta?.total_pages ?? Math.ceil(total / Math.max(1, limit))));
      setRows(normalized);
      setPage(backendPage);
      setMeta({ total, page: backendPage, limit, totalPages });
    } catch (error) {
      if (requestSeqRef.current !== requestId) return;
      console.error('[ImportDocToPOSModal] Error cargando documentos:', error);
      setRows([]);
      setPage(1);
      setMeta({ total: 0, page: 1, limit: effectiveLimit, totalPages: 1 });
    } finally {
      if (requestSeqRef.current === requestId) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setTab(TAB_PEDIDOS);
    setQ('');
    setRows([]);
    setShowUnavailable(false);
    setShowOnlyPendingDte(false);
    setEstadoFilter('todos');
    setPage(1);
    setMeta({ total: 0, page: 1, limit: DEFAULT_LIMIT, totalPages: 1 });
    fetchRows({ nextTab: TAB_PEDIDOS, queryArg: '', nextPageArg: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestSeqRef.current += 1;
  }, []);

  const handleTabChange = (_event, nextTab) => {
    setTab(nextTab);
    setQ('');
    setRows([]);
    setPage(1);
    setShowUnavailable(false);
    setShowOnlyPendingDte(false);
    setEstadoFilter('todos');
    fetchRows({ nextTab, queryArg: '', nextPageArg: 1 });
  };

  const handleSearchChange = (value) => {
    setQ(value);
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchRows({ nextTab: tab, queryArg: value.trim(), nextPageArg: 1 }), 300);
  };

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (!showUnavailable && row.blocked) return false;
    if (tab === TAB_PEDIDOS && showOnlyPendingDte && !row.puedeEmitirPendientes) return false;
    if (tab === TAB_PEDIDOS && estadoFilter !== 'todos') {
      const filter = cleanUpper(estadoFilter);
      const state = cleanUpper(row.estado);
      if (filter === 'APPROVED' && !['APPROVED', 'APROBADO'].includes(state)) return false;
      if (filter === 'PENDING' && !['PENDING', 'PENDIENTE'].includes(state)) return false;
      if (!['APPROVED', 'PENDING'].includes(filter) && state !== filter) return false;
    }
    return true;
  }), [rows, showUnavailable, showOnlyPendingDte, estadoFilter, tab]);

  const summary = useMemo(() => ({
    available: rows.filter((row) => !row.blocked).length,
    blocked: rows.filter((row) => row.blocked).length,
    partial: rows.filter((row) => row.puedeEmitirPendientes).length,
    loaded: rows.length,
  }), [rows]);

  const doImport = async (row) => {
    if (!row?.id || disabled || row.blocked) return;
    const key = `${row.kind === TAB_MANTENCIONES ? 'mantencion' : 'pedido'}:${row.id}`;
    setImportingKey(key);
    try {
      if (row.kind === TAB_MANTENCIONES) {
        const { data } = await api.get(`/automotriz_mantenciones/${row.id}/pos-contexto`);
        const context = data?.pos_contexto || data?.contexto || data;
        const items = Array.isArray(context?.items) ? context.items : [];
        if (!items.length) throw new Error('La mantención no tiene ítems cobrables para cargar al POS.');
        await onImport?.(SOURCE_TYPE_MANTENCION, row.id, {
          row,
          detalle: data,
          pos_contexto: context,
          modo_importacion: SOURCE_TYPE_MANTENCION,
          items_para_pos: items,
        });
      } else {
        const { data: detalle } = await api.get(`/ventas_pedidos/${row.id}`);
        const sourceType = getModoImportacionPedido(detalle) || row.modoImportacion || SOURCE_TYPE_PEDIDO;
        const pedido = buildPedidoForPOS(detalle, sourceType);
        const items = Array.isArray(pedido.items_para_pos) ? pedido.items_para_pos : [];
        if (sourceType === SOURCE_TYPE_PENDING_DTE && !items.length) {
          throw new Error('El pedido no tiene ítems pendientes de facturación para cargar al POS.');
        }
        await onImport?.(sourceType, row.id, {
          pedido,
          detalle: pedido,
          row,
          modo_importacion: sourceType,
          puede_emitir_pendientes: sourceType === SOURCE_TYPE_PENDING_DTE,
          cantidad_pendiente_dte: pedido?.cantidad_pendiente_dte ?? pedido?.facturacion?.cantidad_pendiente ?? row.cantidadPendiente ?? 0,
          items_para_pos: items,
          items_pendientes: pedido.items_pendientes_facturacion || [],
        });
      }
      onClose?.();
    } catch (error) {
      console.error('[ImportDocToPOSModal] Error importando:', error);
      alert(error?.response?.data?.error || error?.response?.data?.message || error?.message || 'No se pudo importar el documento.');
    } finally {
      setImportingKey(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="body" PaperProps={{ sx: { borderRadius: 3, overflow: 'visible', maxHeight: 'none' } }}>
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Importar documento al POS</Typography>
            <Typography variant="body2" color="text.secondary">Carga pedidos o mantenciones listas para cobro.</Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
        <Tabs value={tab} onChange={handleTabChange} sx={{ mt: 1 }}>
          <Tab value={TAB_PEDIDOS} icon={<ReceiptLongIcon />} iconPosition="start" label="Pedidos" />
          <Tab value={TAB_MANTENCIONES} icon={<BuildCircleOutlinedIcon />} iconPosition="start" label="Mantenciones" />
        </Tabs>
      </DialogTitle>

      {loading ? <LinearProgress /> : null}

      <DialogContent sx={{ bgcolor: 'grey.50', overflow: 'visible', px: 3, py: 2 }}>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}><SummaryCard title="Disponibles" value={summary.available} helper={tab === TAB_MANTENCIONES ? 'Listas para cobrar' : 'Pedidos nuevos o parciales'} icon={<CheckCircleIcon />} color="success" /></Grid>
          <Grid item xs={12} sm={4}><SummaryCard title={tab === TAB_MANTENCIONES ? 'No disponibles' : 'DTE parcial'} value={tab === TAB_MANTENCIONES ? summary.blocked : summary.partial} helper={tab === TAB_MANTENCIONES ? 'Ya vinculadas o cerradas' : 'Con ítems pendientes'} icon={tab === TAB_MANTENCIONES ? <BlockIcon /> : <ReceiptLongIcon />} color={tab === TAB_MANTENCIONES ? 'error' : 'warning'} /></Grid>
          <Grid item xs={12} sm={4}><SummaryCard title="Cargados" value={summary.loaded} helper={`Total backend: ${meta.total}`} icon={<ShoppingCartCheckoutIcon />} color="primary" /></Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={q}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder={tab === TAB_MANTENCIONES ? 'Buscar por código, patente, VIN, cliente, marca o modelo…' : 'Buscar por pedido, cliente, RUT, VIN o teléfono…'}
              disabled={disabled}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">{loading ? <CircularProgress size={18} /> : q ? <IconButton size="small" onClick={() => { setQ(''); fetchRows({ nextTab: tab, queryArg: '', nextPageArg: 1 }); }}><CloseIcon fontSize="small" /></IconButton> : null}</InputAdornment>,
              }}
            />

            {tab === TAB_PEDIDOS ? (
              <>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Estado</InputLabel>
                  <Select value={estadoFilter} label="Estado" onChange={(event) => setEstadoFilter(event.target.value)}>
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="approved">Aprobados</MenuItem>
                    <MenuItem value="pending">Pendientes</MenuItem>
                    <MenuItem value="cancelado">Cancelados</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel control={<Switch checked={showOnlyPendingDte} onChange={(event) => setShowOnlyPendingDte(event.target.checked)} size="small" />} label="Solo DTE parcial" />
              </>
            ) : null}

            <FormControlLabel control={<Switch checked={showUnavailable} onChange={(event) => setShowUnavailable(event.target.checked)} size="small" />} label="Mostrar no disponibles" />
            <Tooltip title="Refrescar"><span><IconButton onClick={() => fetchRows({ nextTab: tab, queryArg: q, nextPageArg: page })} disabled={disabled || loading}><RefreshIcon /></IconButton></span></Tooltip>
          </Stack>
        </Paper>

        {disabled ? (
          <Alert severity="warning">Debes tener la caja abierta y conexión activa para importar documentos.</Alert>
        ) : (
          <>
            <Stack spacing={1.5}>
              {visibleRows.length ? visibleRows.map((row) => row.kind === TAB_MANTENCIONES ? (
                <MantencionCard key={`mantencion-${row.id}`} row={row} fmtCLP={fmtCLP} fmtDate={fmtDate} disabled={disabled} importingKey={importingKey} onImport={doImport} />
              ) : (
                <PedidoCard key={`pedido-${row.id}`} row={row} fmtCLP={fmtCLP} fmtDate={fmtDate} disabled={disabled} importingKey={importingKey} onImport={doImport} />
              )) : (
                <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                  <Stack alignItems="center" spacing={1}>
                    {loading ? <CircularProgress size={28} /> : <WarningAmberIcon color="disabled" />}
                    <Typography color="text.secondary">{loading ? 'Buscando documentos…' : `Sin ${tab === TAB_MANTENCIONES ? 'mantenciones' : 'pedidos'} disponibles.`}</Typography>
                  </Stack>
                </Paper>
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Mostrando {visibleRows.length} de {rows.length}. Total backend: {meta.total}. Página {page} de {meta.totalPages}.</Typography>
              <Pagination page={page} count={Math.max(1, meta.totalPages)} onChange={(_event, nextPage) => { setPage(nextPage); fetchRows({ nextTab: tab, queryArg: q, nextPageArg: nextPage }); }} color="primary" shape="rounded" size="small" disabled={loading} />
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
